/**
 * Mini 12 Camera Product Page
 * Handles: gallery grid, mobile swiper, zoom modal, variant picking,
 *          gallery image reorder on variant change, add-ons, add to cart.
 */
(function () {
  'use strict';

  /* ── iOS-safe scroll lock ──────────────────────────────────────────────
     `overflow: hidden` on <body> does NOT hold the scroll position on iOS
     Safari — toggling it (modal open/close) loses the offset and dumps the
     user at the top of the page. Pin the body with position:fixed at the
     saved offset and restore it on release. Pause Lenis while locked so its
     RAF loop doesn't fight the fixed body. */
  var _m12cScrollY = 0;
  function m12cLockScroll() {
    if (document.body.classList.contains('m12c-no-scroll')) return;
    _m12cScrollY = window.scrollY || window.pageYOffset || 0;
    if (typeof lenis !== 'undefined' && lenis && lenis.stop) lenis.stop();
    document.body.style.top = '-' + _m12cScrollY + 'px';
    document.body.classList.add('m12c-no-scroll');
  }
  function m12cUnlockScroll() {
    if (!document.body.classList.contains('m12c-no-scroll')) return;
    document.body.classList.remove('m12c-no-scroll');
    document.body.style.top = '';
    window.scrollTo(0, _m12cScrollY);
    if (typeof lenis !== 'undefined' && lenis && lenis.start) lenis.start();
  }

  class Mini12Camera {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      // Out-of-stock variants are selectable now, so the buy buttons carry the
      // message instead of the swatch being locked. Label comes from the
      // section setting via a data attribute so Liquid stays the one source.
      this.comingSoonLabel = root.dataset.comingSoonLabel || 'Coming Soon';

      // Parse embedded product data
      const dataEl = document.getElementById('m12c-data-' + this.sectionId);
      if (!dataEl) return;
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[Mini12Camera] Failed to parse product data', e);
        return;
      }

      // Ordered images (mutated on variant change)
      this.orderedImages = [...this.data.images];

      // Current selected variant
      this.currentVariant = this.data.variants.find(
        (v) => v.id === this.data.currentVariantId
      ) || this.data.variants[0];

      // DOM references
      this.grid = root.querySelector('.m12c__grid');
      this.swiperWrap = root.querySelector('.m12c__swiper-wrap');
      this.modal = root.querySelector('.m12c__modal');
      this.variantsEl = root.querySelector('.m12c__variants');
      this.priceEl = root.querySelector('.m12c__price');
      this.atcForm = root.querySelector('.m12c__form');
      this.atcBtn = root.querySelector('.m12c__atc-btn');
      this.buyNowBtn = root.querySelector('[data-m12c-buynow]');
      this.atcHintEl = root.querySelector('[data-m12c-atc-hint]');
      this.errorEl = root.querySelector('.m12c__error');
      this.stickyBar = root.querySelector('[data-m12c-sticky]');
      this.stickyBtn = root.querySelector('[data-m12c-sticky-atc]');

      this.gallerySwiper = null;
      this.thumbsSwiper = null;
      this.modalSwiper = null;
      this._engraveSyncing = false;

      this._init();
    }

    _init() {
      this._initGallerySwiper();
      this._initModalSwiper();
      this._bindGridClicks();
      this._bindModalClose();
      this._bindVariantChange();
      this._bindAddToCart();
      this._bindBuyNow();
      this._bindAddonToggles();
      this._bindAccordion();
      this._bindPinChecker();
      this._bindEngrave();
      this._bindPopupSwatches();
      this._syncPopupSwatches();
      this._initStickyAtc();
      this._initCartMonitor();

      // Filter gallery and engraving preview to the selected variant on page load
      if (this.currentVariant) {
        this._updateGalleryImages(this.currentVariant);
        this._updateEngravePreview(this.currentVariant);
      }

      // Engraving is mandatory on engraving products → lock ATC/Buy Now until a name is saved.
      this._updateAtcGate();
    }

    // ─────────────────────────────────────────
    //  Gallery Swiper (mobile)
    // ─────────────────────────────────────────
    _initGallerySwiper() {
      const el = this.swiperWrap
        ? this.swiperWrap.querySelector('.m12c__gallery-swiper')
        : null;
      if (!el || typeof Swiper === 'undefined') return;

      const needsThumbs =
        this.data.desktopGallery === 'thumbnail_swiper' ||
        this.data.mobileGallery === 'thumbnail_swiper';

      if (needsThumbs) {
        const thumbsEl = this.swiperWrap.querySelector('.m12c__thumbs-swiper');
        if (thumbsEl) {
          const isDesktop = window.innerWidth > 1024;
          this.thumbsSwiper = new Swiper(thumbsEl, {
            direction: isDesktop ? 'vertical' : 'horizontal',
            slidesPerView: 'auto',
            spaceBetween: 8,
            watchSlidesProgress: true,
            slideToClickedSlide: true,
          });
        }
      }

      this.gallerySwiper = new Swiper(el, {
        slidesPerView: 1,
        spaceBetween: 0,
        pagination: {
          el: el.querySelector('.swiper-pagination'),
          clickable: true,
          dynamicBullets: true,
        },
        navigation: {
          nextEl: el.querySelector('.swiper-arrow.is-next'),
          prevEl: el.querySelector('.swiper-arrow.is-prev'),
        },
        ...(this.thumbsSwiper ? { thumbs: { swiper: this.thumbsSwiper } } : {}),
        ...(this.data.enableZoom ? {
          on: {
            click: (swiper, event) => {
              if (event.target.closest('.swiper-pagination')) return;
              this._openModal(swiper.activeIndex);
            },
          },
        } : {}),
      });
    }

    // ─────────────────────────────────────────
    //  Modal Swiper (zoom popup)
    // ─────────────────────────────────────────
    _initModalSwiper() {
      const el = this.modal ? this.modal.querySelector('.swiper') : null;
      if (!el || typeof Swiper === 'undefined') return;

      this.modalSwiper = new Swiper(el, {
        slidesPerView: 1,
        spaceBetween: 16,
        zoom: { maxRatio: 3 },
        keyboard: { enabled: true },
        navigation: {
          prevEl: this.modal.querySelector('.m12c__modal-prev'),
          nextEl: this.modal.querySelector('.m12c__modal-next'),
        },
        pagination: {
          el: this.modal.querySelector('.m12c__modal-pagination'),
          clickable: true,
          dynamicBullets: true,
        },
      });
    }

    // ─────────────────────────────────────────
    //  Desktop grid clicks → open modal
    // ─────────────────────────────────────────
    _bindGridClicks() {
      if (!this.grid || !this.data.enableZoom) return;
      this.grid.addEventListener('click', (e) => {
        const item = e.target.closest('[data-gallery-index]');
        if (!item) return;
        this._openModal(parseInt(item.dataset.galleryIndex, 10));
      });
    }

    // ─────────────────────────────────────────
    //  Modal open / close
    // ─────────────────────────────────────────
    _openModal(index) {
      if (!this.modal) return;
      this.modal.removeAttribute('hidden');
      // m12cLockScroll();
      if (this.modalSwiper) {
        this.modalSwiper.slideTo(index, 0, false);
      }
      const closeBtn = this.modal.querySelector('.m12c__modal-close');
      if (closeBtn) closeBtn.focus();
    }

    _closeModal() {
      if (!this.modal) return;
      this.modal.setAttribute('hidden', '');
      // m12cUnlockScroll();
    }

    _bindModalClose() {
      if (!this.modal) return;

      this.modal
        .querySelector('.m12c__modal-close')
        ?.addEventListener('click', () => this._closeModal());

      this.modal
        .querySelector('.m12c__modal-overlay')
        ?.addEventListener('click', () => this._closeModal());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.modal.hasAttribute('hidden')) {
          this._closeModal();
        }
      });
    }

    // ─────────────────────────────────────────
    //  Variant picker
    // ─────────────────────────────────────────
    _bindVariantChange() {
      if (!this.variantsEl) return;
      this.variantsEl.addEventListener('change', (e) => {
        if (e.target.matches('input[type="radio"]')) {
          this._onVariantChange();
        }
      });
    }

    _onVariantChange() {
      // Collect selected option values indexed by option position
      const selectedOptions = [];
      const groups = this.variantsEl.querySelectorAll('.m12c__option-group');

      groups.forEach((group) => {
        const optionIdx = parseInt(group.dataset.optionIndex, 10);
        const checked = group.querySelector('input[type="radio"]:checked');
        if (!checked) return;

        selectedOptions[optionIdx] = checked.value;

        // Update the displayed selected-value label
        const valLabel = group.querySelector('.m12c__option-val');
        if (valLabel) valLabel.textContent = checked.value;

        // Refresh active CSS class on swatch / pill labels
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const lbl = group.querySelector(`label[for="${input.id}"]`);
          if (lbl) lbl.classList.toggle('is-active', input.checked);
        });
      });

      const variant = this._findVariant(selectedOptions);
      if (!variant) return;

      this.currentVariant = variant;

      // Sync hidden variant-ID input
      const hiddenId = this.root.querySelector('[name="id"].product-variant-id');
      if (hiddenId) hiddenId.value = variant.id;

      this._updatePrice(variant);
      this._updateATCButton(variant);
      this._updateAtcGate();
      this._updateSticky(variant);
      this._updateGalleryImages(variant);
      this._updateEngravePreview(variant);
      this._syncPopupSwatches();

      // Update URL without reload
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({ path: url.toString() }, '', url.toString());
      } catch (_) {}
    }

    _findVariant(selectedOptions) {
      return this.data.variants.find((v) =>
        v.options.every((opt, i) => opt === selectedOptions[i])
      );
    }

    // ─────────────────────────────────────────
    //  Price update
    // ─────────────────────────────────────────
    _updatePrice(variant) {
      if (!this.priceEl) return;

      const emiPrice = this._formatMoney(Math.floor(variant.price / 12));

      let html = `
        <div class="m12c__price-main">
          <p class="m12c__price-label">Get Best Price</p>
          <span class="m12c__price-now">${variant.price_formatted}</span>
          <span class="m12c__price-tax">Inc. all taxes</span>
        </div>
        <div class="m12c__price-emi">
          <p class="m12c__price-or">Or</p>
          <span class="m12c__price-emi-amount">${emiPrice}/mo</span>
          <a href="#emi" class="m12c__price-emi-link">Check No Cost EMI Options ›</a>
        </div>`;

      if (variant.compare_at_price > variant.price) {
        const savingsAmt = variant.compare_at_price - variant.price;
        const savingsFormatted = this._formatMoney(savingsAmt);
        html += `
        <div class="m12c__price-savings">
          <span class="m12c__price-mrp">MRP: ${variant.compare_at_price_formatted}</span>
          <span class="m12c__price-saving-badge">You're saving ${savingsFormatted} today.*</span>
        </div>`;
      }

      this.priceEl.innerHTML = html;
    }

    _formatMoney(cents) {
      // Minimal money formatter — mirrors Shopify's default ₹X,XXX.XX format
      const amount = cents / 100;
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    }

    // ─────────────────────────────────────────
    //  ATC button state
    // ─────────────────────────────────────────
    _updateATCButton(variant) {
      if (!this.atcBtn) return;
      const textEl = this.atcBtn.querySelector('.m12c__atc-text');
      const addToCartStr =
        (window.variantStrings && window.variantStrings.addToCart) ||
        'Add to Cart';
      if (variant.available) {
        this.atcBtn.removeAttribute('disabled');
        if (textEl) textEl.textContent = addToCartStr;
      } else {
        this.atcBtn.setAttribute('disabled', 'disabled');
        if (textEl) textEl.textContent = this.comingSoonLabel;
      }

      // Buy Now has no "Coming Soon" state — it is removed entirely, not relabelled.
      if (this.buyNowBtn) {
        this.buyNowBtn.hidden = !variant.available;
        if (variant.available) {
          this.buyNowBtn.removeAttribute('disabled');
        } else {
          this.buyNowBtn.setAttribute('disabled', 'disabled');
        }
      }
    }

    // ─────────────────────────────────────────
    //  Mandatory engraving gate
    //  Engraving products require a saved NAME before checkout. ATC + Buy Now
    //  are greyed (not disabled) until then; clicking opens the popup instead.
    //  Fail-open: non-engraving products (or unconfigured ones) are never gated.
    // ─────────────────────────────────────────
    _engravingRequired() {
      return (
        !!this.root.querySelector('[data-m12c-engrave-property]') &&
        !!this.data.engravingVariantId
      );
    }

    // True only when a typed name is saved — an image-only engraving does NOT unlock.
    _engravingTextSaved() {
      return (('' + (this._engraveSavedValue || '')).trim().length > 0);
    }

    _updateAtcGate() {
      if (!this._engravingRequired()) return; // fail-open: never lock other products
      const locked = !this._engravingTextSaved();
      [this.atcBtn, this.buyNowBtn, this.stickyBtn].forEach((btn) => {
        if (btn) btn.classList.toggle('m12c__atc-locked', locked);
      });
      if (this.atcHintEl) this.atcHintEl.hidden = !locked;
    }

    // Clicking a locked button opens the engraving popup and flashes the hint.
    _flashGate() {
      if (typeof this._openEngravePopup === 'function') this._openEngravePopup();
      if (this.atcHintEl) {
        this.atcHintEl.hidden = false;
        this.atcHintEl.classList.remove('m12c__atc-hint--flash');
        // force reflow so the animation can re-trigger on repeated clicks
        void this.atcHintEl.offsetWidth;
        this.atcHintEl.classList.add('m12c__atc-hint--flash');
      }
    }

    // ─────────────────────────────────────────
    //  Sticky Add to Cart bar
    //  Shown only while the main ATC button is
    //  scrolled out of view → one ATC at a time.
    // ─────────────────────────────────────────
    _initStickyAtc() {
      if (!this.stickyBar || !this.atcBtn) return;

      // Sticky button shares the cart-add flow with the main button
      if (this.stickyBtn) {
        this.stickyBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this._handleAddToCart({ mode: 'cart', btn: this.stickyBtn });
        });
      }

      // Reveal the sticky bar only when the main ATC leaves the viewport
      if ('IntersectionObserver' in window) {
        this._stickyObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const show = !entry.isIntersecting;
              this.stickyBar.classList.toggle('is-visible', show);
              this.stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
            });
          },
          { threshold: 0 }
        );
        this._stickyObserver.observe(this.atcBtn);
      }

      this._updateSticky(this.currentVariant);
    }

    _updateSticky(variant) {
      if (!this.stickyBar || !variant) return;

      const priceEl = this.stickyBar.querySelector('[data-m12c-sticky-price]');
      if (priceEl && variant.price_formatted) priceEl.textContent = variant.price_formatted;

      if (!this.stickyBtn) return;
      const textEl = this.stickyBtn.querySelector('.m12c__sticky-btn-text');
      const addToCartStr =
        (window.variantStrings && window.variantStrings.addToCart) || 'Add to Cart';
      if (variant.available) {
        this.stickyBtn.removeAttribute('disabled');
        if (textEl) textEl.textContent = addToCartStr;
      } else {
        this.stickyBtn.setAttribute('disabled', 'disabled');
        if (textEl) textEl.textContent = this.comingSoonLabel;
      }
    }

    // ─────────────────────────────────────────
    //  Filter images for a given variant
    //
    //  Logic: the featured image of each variant is a "section divider"
    //  in the product images list.  All images from that featured image's
    //  position up to (but not including) the NEXT variant's featured
    //  image position belong to this variant.  The last variant owns
    //  everything from its featured image to the end of the list.
    //
    //  Example (50 images, 5 variants, 10 images each):
    //    variant 1 featured = image[0]  → slice [0, 10)
    //    variant 2 featured = image[10] → slice [10, 20)
    //    ...
    //    variant 5 featured = image[40] → slice [40, 50)
    // ─────────────────────────────────────────
    _getImagesForVariant(variantId) {
      // Single-variant product — show the full catalogue
      if (this.data.variants.length <= 1) {
        return this.data.images;
      }

      const variant = this.data.variants.find((v) => v.id === variantId);

      // No featured image assigned → show all images
      if (!variant || !variant.featured_image_id) {
        return this.data.images;
      }

      // Where does this variant's block start?
      const startIdx = this.data.images.findIndex(
        (img) => img.id === variant.featured_image_id
      );
      if (startIdx === -1) return this.data.images;

      // Collect all featured-image positions that come AFTER startIdx
      const laterStarts = this.data.variants
        .filter((v) => v.featured_image_id && v.id !== variantId)
        .map((v) => this.data.images.findIndex((img) => img.id === v.featured_image_id))
        .filter((idx) => idx > startIdx)
        .sort((a, b) => a - b);

      // End is the nearest later start, or end of array
      const endIdx = laterStarts.length > 0 ? laterStarts[0] : this.data.images.length;

      return this.data.images.slice(startIdx, endIdx);
    }

    // ─────────────────────────────────────────
    //  Gallery update on variant change (full rebuild)
    // ─────────────────────────────────────────
    _updateGalleryImages(variant) {
      const images = this._getImagesForVariant(variant.id);
      this.orderedImages = images;
      this._renderGrid(images);
      this._refreshMobileSwiper(images);
      this._refreshModalSwiper(images);
    }

    _renderGrid(images) {
      if (!this.grid) return;
      const single = images.length === 1;
      this.grid.innerHTML = images.map((img, i) => `
        <button
          class="m12c__grid-item${single ? ' is-single' : ''}"
          type="button"
          data-gallery-index="${i}"
          aria-label="Zoom image ${i + 1}"
        >
          <img
            src="${this._esc(img.srcMedium)}"
            srcset="${this._esc(img.srcMedium)} 800w, ${this._esc(img.src)} 1600w"
            sizes="(max-width: 767px) 45vw, (max-width: 1024px) 45vw, 26vw"
            alt="${this._esc(img.alt)}"
            loading="${i === 0 ? 'eager' : 'lazy'}"
          >
        </button>
      `).join('');
    }

    _refreshMobileSwiper(images) {
      if (!this.swiperWrap) return;

      // Fully destroy both swipers — innerHTML rebuild breaks Swiper's
      // internal slide tracking and controller sync if we only call update()
      if (this.gallerySwiper) {
        this.gallerySwiper.destroy(true, true);
        this.gallerySwiper = null;
      }
      if (this.thumbsSwiper) {
        this.thumbsSwiper.destroy(true, true);
        this.thumbsSwiper = null;
      }

      // Rebuild gallery slide HTML
      const galleryWrap = this.swiperWrap.querySelector('.m12c__gallery-swiper .swiper-wrapper');
      if (galleryWrap) {
        galleryWrap.innerHTML = images.map((img, i) => `
          <div class="swiper-slide m12c__gallery-slide" data-gallery-index="${i}">
            <img
              src="${this._esc(img.srcMedium)}"
              alt="${this._esc(img.alt)}"
              loading="${i === 0 ? 'eager' : 'lazy'}"
            >
          </div>
        `).join('');
      }

      // Rebuild thumbs slide HTML
      const thumbsWrap = this.swiperWrap.querySelector('.m12c__thumbs-swiper .swiper-wrapper');
      if (thumbsWrap) {
        thumbsWrap.innerHTML = images.map((img) => `
          <div class="swiper-slide m12c__thumbs-slide">
            <img src="${this._esc(img.srcMedium)}" alt="${this._esc(img.alt)}" loading="lazy">
          </div>
        `).join('');
      }

      // Reinit from scratch — controller linkage and active tracking are clean
      this._initGallerySwiper();
    }

    _refreshModalSwiper(images) {
      if (!this.modalSwiper) return;
      const wrapper = this.modalSwiper.wrapperEl;
      if (!wrapper) return;

      wrapper.innerHTML = images.map((img) => `
        <div class="swiper-slide m12c__modal-slide">
          <div class="swiper-zoom-container">
            <img
              src="${this._esc(img.src)}"
              srcset="${this._esc(img.srcMedium)} 800w, ${this._esc(img.src)} 1600w"
              sizes="90vw"
              alt="${this._esc(img.alt)}"
              loading="lazy"
            >
          </div>
        </div>
      `).join('');

      this.modalSwiper.update();
    }

    // Minimal HTML attribute escaper — prevents XSS from alt/src values
    _esc(str) {
      return String(str || '').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    // ─────────────────────────────────────────
    //  Engraving preview image — swap on variant change
    // ─────────────────────────────────────────
    _updateEngravePreview(variant) {
      if (!variant) return;
      const popupImg   = this.root.querySelector('.m12c__engrave-popup-img');
      const thumbImg   = this.root.querySelector('.m12c__engrave-thumb-img');
      if (variant.backImg && popupImg)   popupImg.src   = variant.backImg;
      if (variant.backImgThumb && thumbImg) thumbImg.src = variant.backImgThumb;
    }

    // ─────────────────────────────────────────
    //  Popup variant swatches (mirror main picker)
    // ─────────────────────────────────────────
    _bindPopupSwatches() {
      const popupSwatchGroup = this.root.querySelector('[data-m12c-popup-swatches]');
      if (!popupSwatchGroup || !this.variantsEl) return;
      const optionPosition = parseInt(popupSwatchGroup.dataset.optionPosition, 10);
      const mainGroup = this.variantsEl.querySelector(
        `.m12c__option-group[data-option-index="${optionPosition - 1}"]`
      );
      if (!mainGroup) return;

      popupSwatchGroup.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-m12c-popup-swatch]');
        // .is-disabled is only the out-of-stock LOOK — still selectable.
        if (!btn) return;
        const value = btn.dataset.value;
        const radio = mainGroup.querySelector(`input[type="radio"][value="${CSS.escape(value)}"]`);
        if (!radio) return;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    _syncPopupSwatches() {
      const popupSwatchGroup = this.root.querySelector('[data-m12c-popup-swatches]');
      if (!popupSwatchGroup || !this.currentVariant) return;
      const optionPosition = parseInt(popupSwatchGroup.dataset.optionPosition, 10);
      const selectedValue = this.currentVariant.options[optionPosition - 1];
      popupSwatchGroup.querySelectorAll('[data-m12c-popup-swatch]').forEach((btn) => {
        btn.classList.toggle('is-active', btn.dataset.value === selectedValue);
      });
    }

    // ─────────────────────────────────────────
    //  Add-on checkboxes
    // ─────────────────────────────────────────
    _bindAddonToggles() {
      this.root.querySelectorAll('.m12c__addon-check').forEach((chk) => {
        chk.addEventListener('change', () => {
          const item = chk.closest('.m12c__addon-item');
          if (item) item.classList.toggle('is-selected', chk.checked);
        });
      });
    }

    // ─────────────────────────────────────────
    //  Add to cart (main product + selected add-ons)
    // ─────────────────────────────────────────
    _bindAddToCart() {
      if (!this.atcForm) return;
      this.atcForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this._handleAddToCart({ mode: 'cart', btn: this.atcBtn });
      });
    }

    // ─────────────────────────────────────────
    //  Buy Now (main product + selected add-ons → straight to checkout)
    // ─────────────────────────────────────────
    _bindBuyNow() {
      if (!this.buyNowBtn) return;
      this.buyNowBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this._handleAddToCart({ mode: 'checkout', btn: this.buyNowBtn });
      });
    }

    async _handleAddToCart(opts) {
      opts = opts || {};
      const mode = opts.mode || 'cart';
      const btn = opts.btn || this.atcBtn;

      if (!this.currentVariant || !this.currentVariant.available) return;

      // Mandatory engraving: block ATC / Buy Now / sticky until a name is saved.
      // Opens the popup (and flashes the hint) instead of adding to cart.
      if (this._engravingRequired() && !this._engravingTextSaved()) {
        this._flashGate();
        return;
      }

      // Re-entrancy lock: a fast double-submit (double-click, or Enter pressed
      // twice on the form) before the first /cart/add.js resolves would add the
      // whole set — camera + engraving + add-ons — twice. `aria-disabled` alone
      // does not block a form submit, so guard explicitly here.
      if (this._addingToCart) return;
      this._addingToCart = true;

      const spinner = btn ? btn.querySelector('.loading__spinner') : null;
      const textEl = btn
        ? btn.querySelector('.m12c__atc-text') ||
          btn.querySelector('.m12c__buynow-text')
        : null;
      const originalText = textEl ? textEl.textContent : '';

      // Collect selected add-on variant IDs
      const selectedAddons = Array.from(
        this.root.querySelectorAll('.m12c__addon-check:checked')
      ).map((chk) => ({ id: parseInt(chk.value, 10), quantity: 1 }));

      const engraveInput = this.root.querySelector('[data-m12c-engrave-property]');
      const engravingName = engraveInput ? engraveInput.value.trim() : '';
      const engravingImage = this._engraveSavedFile || null;
      const hasEngraving = (engravingName || engravingImage) && this.data.engravingVariantId;

      // Camera line + selected add-ons
      const cameraItem = { id: this.currentVariant.id, quantity: 1 };
      const items = [cameraItem, ...selectedAddons];

      // Engraving service variant — added only when a name OR image is provided
      // and the section setting is configured. The visible name lives on this line
      // (so it shows in cart/order). The camera line gets a hidden `_has_engraving`
      // marker and the engraving line gets a hidden `_for_variant` link, so the
      // global engraving guard can keep the two as a bonded pair (remove either →
      // remove the other), site-wide. See assets/engraving-guard.js.
      if (hasEngraving) {
        cameraItem.properties = { _has_engraving: '1' };

        const engraveItem = {
          id: this.data.engravingVariantId,
          quantity: 1,
          properties: { _for_variant: this.currentVariant.id },
        };
        if (engravingName) engraveItem.properties.Engraving = engravingName;
        // Image is attached via FormData below (cannot be JSON-encoded)
        items.push(engraveItem);

        // Grace window: tell the global guard not to treat the freshly-added
        // pair as orphaned for a few seconds (covers slow cart re-renders).
        window.__engraveAddGrace = Date.now() + 6000;
      }

      // Enter loading state
      if (btn) {
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('loading');
      }
      if (spinner) spinner.classList.remove('hidden');
      if (textEl) textEl.textContent = '...';

      // Buy Now skips the drawer; cart mode looks for a notification/drawer
      const cartEl =
        mode === 'checkout'
          ? null
          : document.querySelector('cart-notification') ||
            document.querySelector('cart-drawer');

      try {
        const sectionIds =
          cartEl && typeof cartEl.getSectionsToRender === 'function'
            ? cartEl.getSectionsToRender().map((s) => s.id)
            : null;
        if (cartEl && typeof cartEl.setActiveElement === 'function') {
          cartEl.setActiveElement(document.activeElement);
        }

        let fetchOpts;
        if (engravingImage && hasEngraving) {
          // FormData path — required to attach a File as a line item property
          const fd = new FormData();
          items.forEach((it, idx) => {
            fd.append(`items[${idx}][id]`, it.id);
            fd.append(`items[${idx}][quantity]`, it.quantity);
            if (it.properties) {
              Object.keys(it.properties).forEach((k) => {
                fd.append(`items[${idx}][properties][${k}]`, it.properties[k]);
              });
            }
          });
          // Attach image to the engraving line item (always the last entry when hasEngraving)
          const engraveIdx = items.length - 1;
          fd.append(`items[${engraveIdx}][properties][Engraving Image]`, engravingImage, engravingImage.name);
          if (sectionIds) {
            fd.append('sections', sectionIds.join(','));
            fd.append('sections_url', window.location.pathname);
          }
          fetchOpts = {
            method: 'POST',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            body: fd,
          };
        } else {
          const bodyObj = { items };
          if (sectionIds) {
            bodyObj.sections = sectionIds;
            bodyObj.sections_url = window.location.pathname;
          }
          fetchOpts = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(bodyObj),
          };
        }

        const response = await fetch('/cart/add.js', fetchOpts);

        const result = await response.json();

        if (result.status) {
          this._showError(result.description || result.message || 'Error adding to cart');
        } else {
          this._hideError();

          if (mode === 'checkout') {
            window.location = '/checkout';
            return;
          }

          if (cartEl) {
            if (cartEl.classList.contains('is-empty')) {
              cartEl.classList.remove('is-empty');
            }
            if (typeof cartEl.renderContents === 'function') {
              cartEl.renderContents(result);
            }
          } else {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'mini12camera-product',
              productVariantId: this.currentVariant.id,
              cartData: result,
            });
          }
        }
      } catch (err) {
        console.error('[Mini12Camera] add to cart error:', err);
        this._showError('Something went wrong. Please try again.');
      } finally {
        this._addingToCart = false;
        if (btn) {
          btn.classList.remove('loading');
          btn.removeAttribute('aria-disabled');
        }
        if (spinner) spinner.classList.add('hidden');

        if (textEl) {
          if (textEl.classList.contains('m12c__atc-text')) {
            const addToCartStr =
              (window.variantStrings && window.variantStrings.addToCart) ||
              'Add to Cart';
            textEl.textContent = this.currentVariant.available
              ? addToCartStr
              : this.comingSoonLabel;
          } else {
            // Buy Now button — restore its original label
            textEl.textContent = originalText || 'Buy It Now';
          }
        }
      }
    }

    // ─────────────────────────────────────────
    //  Product details accordion
    // ─────────────────────────────────────────
_bindAccordion() {
  this.root.querySelectorAll('[data-m12c-accordion-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const currentItem = btn.closest('.m12c__detail-item');
      const currentBody = currentItem.querySelector('.m12c__detail-body');
      const isOpen = currentItem.classList.contains('is-open');

      // Close all accordions
      this.root.querySelectorAll('.m12c__detail-item').forEach((item) => {
        item.classList.remove('is-open');

        const toggleBtn = item.querySelector('[data-m12c-accordion-toggle]');
        const body = item.querySelector('.m12c__detail-body');

        toggleBtn?.setAttribute('aria-expanded', 'false');
        body?.setAttribute('aria-hidden', 'true');
      });

      // Open clicked accordion only if it was closed
      if (!isOpen) {
        currentItem.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
        currentBody.setAttribute('aria-hidden', 'false');
      }
    });
  });
}

    // ─────────────────────────────────────────
    //  PIN delivery checker
    // ─────────────────────────────────────────
    _bindPinChecker() {
      const btn = this.root.querySelector('[data-m12c-pin-check]');
      if (!btn) return;

      const resultId = btn.getAttribute('aria-controls');
      const resultEl = resultId ? document.getElementById(resultId) : null;
      const inputEl = this.root.querySelector('.m12c__delivery-input');

      btn.addEventListener('click', () => {
        if (!inputEl || !resultEl) return;
        const pin = inputEl.value.replace(/\D/g, '');

        if (pin.length !== 6) {
          resultEl.style.color = '#c00';
          resultEl.textContent = 'Please enter a valid 6-digit PIN code.';
          return;
        }

        btn.disabled = true;
        resultEl.style.color = '';
        resultEl.textContent = 'Checking...';

        // Simulate delivery estimate (replace with real API if available)
        setTimeout(() => {
          const today = new Date();
          today.setDate(today.getDate() + 3);
          const opts = { weekday: 'long', month: 'short', day: 'numeric' };
          const dateStr = today.toLocaleDateString('en-IN', opts);
          resultEl.style.color = 'rgb(var(--color-foreground, 51 51 51))';
          resultEl.textContent = `Estimated delivery by ${dateStr} to PIN ${pin}.`;
          btn.disabled = false;
        }, 600);
      });

      // Allow Enter key in input
      if (inputEl) {
        inputEl.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') btn.click();
        });
      }
    }

    // ─────────────────────────────────────────
    //  Engraving popup
    // ─────────────────────────────────────────
    _bindEngrave() {
      const openBtn   = this.root.querySelector('[data-m12c-engrave-open]');
      if (!openBtn) return;

      // Popup lives at section level, outside .m12c__inner
      const section   = this.root;
      const popup     = section.querySelector('[data-m12c-engrave-popup]');
      if (!popup) return;

      const input     = popup.querySelector('[data-m12c-engrave-input]');
      const popupName = popup.querySelector('[data-m12c-engrave-popup-name]');
      const thumbName = openBtn.querySelector('[data-m12c-engrave-thumb-name]');
      const clearBtn  = popup.querySelector('[data-m12c-engrave-clear]');
      const saveBtn   = popup.querySelector('[data-m12c-engrave-save]');
      const cancelBtn = popup.querySelector('[data-m12c-engrave-cancel]');
      const closeBtns = popup.querySelectorAll('[data-m12c-engrave-close]');
      const termsCheck = popup.querySelector('[data-m12c-engrave-terms]');
      const termsText  = popup.querySelector('[data-m12c-engrave-terms-text]');

      // SAVE is gated by the terms checkbox (when present).
      const syncSaveEnabled = () => {
        if (!saveBtn) return;
        const ok = !termsCheck || termsCheck.checked;
        saveBtn.disabled = !ok;
      };

      const MAX   = 10;
      // Font size: linearly interpolate from maxPx (0 chars) to minPx (MAX chars)
      const calcSize = (len, maxPx, minPx) => {
        if (len === 0) return maxPx;
        return Math.max(minPx, Math.round(maxPx - (len / MAX) * (maxPx - minPx)));
      };

      this._engraveSavedValue = '';
      this._engraveSavedFile = null;
      this._engraveTermsAccepted = false;
      const propertyInput = this.root.querySelector('[data-m12c-engrave-property]');

      // Image upload elements
      const fileInput     = popup.querySelector('[data-m12c-engrave-image]');
      const uploadDrop    = popup.querySelector('[data-m12c-engrave-upload-drop]');
      const uploadEmpty   = popup.querySelector('[data-m12c-engrave-upload-empty]');
      const uploadPreview = popup.querySelector('[data-m12c-engrave-upload-preview]');
      const uploadThumb   = popup.querySelector('[data-m12c-engrave-upload-thumb]');
      const uploadName    = popup.querySelector('[data-m12c-engrave-upload-name]');
      const uploadRemove  = popup.querySelector('[data-m12c-engrave-upload-remove]');

      let transientFile = null;
      let transientThumbUrl = null;

      const renderFileState = (file) => {
        if (transientThumbUrl) {
          URL.revokeObjectURL(transientThumbUrl);
          transientThumbUrl = null;
        }
        if (file) {
          transientThumbUrl = URL.createObjectURL(file);
          if (uploadThumb) uploadThumb.src = transientThumbUrl;
          if (uploadName) uploadName.textContent = file.name;
          if (uploadPreview) uploadPreview.removeAttribute('hidden');
          if (uploadDrop) uploadDrop.classList.add('has-file');
        } else {
          if (uploadThumb) uploadThumb.removeAttribute('src');
          if (uploadName) uploadName.textContent = '';
          if (uploadPreview) uploadPreview.setAttribute('hidden', '');
          if (uploadDrop) uploadDrop.classList.remove('has-file');
        }
      };

      const updatePreview = (val) => {
        const popupPx = calcSize(val.length, 20, 16);
        const thumbPx = calcSize(val.length, 10, 5);
        if (popupName) {
          popupName.textContent = val;
          popupName.style.fontSize = popupPx + 'px';
        }
        if (thumbName) {
          thumbName.textContent = val;
          thumbName.style.fontSize = thumbPx + 'px';
        }
        if (clearBtn) clearBtn.classList.toggle('is-visible', val.length > 0);
      };

      const openPopup = () => {
        // Restore transient state to last committed values
        transientFile = this._engraveSavedFile;
        renderFileState(transientFile);
        if (fileInput) fileInput.value = '';
        // Restore terms acceptance to last saved state, then re-gate SAVE.
        if (termsCheck) termsCheck.checked = this._engraveTermsAccepted;
        syncSaveEnabled();
        popup.removeAttribute('hidden');
        // m12cLockScroll();
        if (input) { input.focus(); updatePreview(input.value); }
      };

      const closePopup = () => {
        popup.setAttribute('hidden', '');
        // m12cUnlockScroll();
      };

      // Expose for the mandatory-engraving gate (clicking a locked ATC opens this).
      this._openEngravePopup = openPopup;

      const revertOnDismiss = () => {
        if (input) { input.value = this._engraveSavedValue; updatePreview(this._engraveSavedValue); }
        transientFile = this._engraveSavedFile;
        renderFileState(transientFile);
        if (fileInput) fileInput.value = '';
        if (termsCheck) termsCheck.checked = this._engraveTermsAccepted;
        syncSaveEnabled();
      };

      openBtn.addEventListener('click', openPopup);

      // Close / cancel — revert to last saved value
      closeBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          revertOnDismiss();
          closePopup();
        });
      });
      if (cancelBtn) {
        cancelBtn.addEventListener('click', () => {
          revertOnDismiss();
          closePopup();
        });
      }

      // Save — commit value and sync to cart property input (gated by terms)
      if (saveBtn) {
        saveBtn.addEventListener('click', () => {
          if (termsCheck && !termsCheck.checked) return; // gate
          this._engraveSavedValue = input ? input.value : '';
          this._engraveSavedFile = transientFile;
          this._engraveTermsAccepted = termsCheck ? termsCheck.checked : true;
          if (propertyInput) propertyInput.value = this._engraveSavedValue;
          closePopup();
          // Saving a name unlocks ATC / Buy Now (clearing it re-locks on next save).
          this._updateAtcGate();
        });
      }

      // Terms checkbox — gate SAVE; clicking the text (not its links) toggles it;
      // links open in a new tab.
      if (termsCheck) {
        termsCheck.addEventListener('change', syncSaveEnabled);
      }
      if (termsText) {
        termsText.querySelectorAll('a').forEach((a) => {
          a.setAttribute('target', '_blank');
          a.setAttribute('rel', 'noopener');
          a.addEventListener('click', (e) => e.stopPropagation());
        });
      }

      // Live preview while typing
      if (input) {
        input.addEventListener('input', () => updatePreview(input.value));
      }

      // Clear button
      if (clearBtn) {
        clearBtn.addEventListener('click', () => {
          if (input) { input.value = ''; updatePreview(''); input.focus(); }
        });
      }

      // File upload — transient until SAVE
      if (fileInput) {
        fileInput.addEventListener('change', () => {
          const file = fileInput.files && fileInput.files[0];
          if (!file) return;
          transientFile = file;
          renderFileState(transientFile);
        });
      }
      if (uploadRemove) {
        uploadRemove.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          transientFile = null;
          if (fileInput) fileInput.value = '';
          renderFileState(null);
        });
      }

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !popup.hasAttribute('hidden')) {
          revertOnDismiss();
          closePopup();
        }
      });
    }

    // ─────────────────────────────────────────
    //  Debounce utility
    // ─────────────────────────────────────────
    _debounce(fn, delay) {
      let t;
      return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), delay); };
    }

    // ─────────────────────────────────────────
    //  Cart monitor — keep engraving product in
    //  sync with the main product's cart state
    // ─────────────────────────────────────────
    _initCartMonitor() {
      if (!this.data.engravingVariantId) return;

      const check = this._debounce(() => {
        if (!this._engraveSyncing) this._syncEngravingCart();
      }, 80);

      // Theme pub/sub (Dawn and similar)
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, check);
      }

      // Fallback: watch the cart drawer for any DOM re-render
      const attachObserver = () => {
        const cartEl = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        if (cartEl) {
          new MutationObserver(check).observe(cartEl, { childList: true, subtree: true });
        }
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', attachObserver);
      } else {
        attachObserver();
      }
    }

    _syncEngravingCart() {
      const engVariantId = this.data.engravingVariantId;
      if (!engVariantId) return;

      // Removal (forward + reverse bonded pair) is owned by the GLOBAL guard
      // (assets/engraving-guard.js), so it works on every page, not just here.
      // This per-section monitor only clears the PDP popup UI once the engraving
      // line has left the cart.
      const cartEl = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const renderedVarIds = new Set(
        Array.from((cartEl || document).querySelectorAll('[data-variant-id]'))
          .map(el => parseInt(el.dataset.variantId, 10))
          .filter(Boolean)
      );

      if (!renderedVarIds.has(engVariantId) && this._engraveSavedValue) {
        this._clearEngravingUI();
      }
    }

    // ─────────────────────────────────────────
    //  Clear all engraving UI state
    // ─────────────────────────────────────────
    _clearEngravingUI() {
      this._engraveSavedValue = '';
      this._engraveSavedFile = null;
      this._engraveTermsAccepted = false;

      const propertyInput = this.root.querySelector('[data-m12c-engrave-property]');
      if (propertyInput) propertyInput.value = '';

      const termsCheck = this.root.querySelector('[data-m12c-engrave-terms]');
      if (termsCheck) termsCheck.checked = false;
      const saveBtn = this.root.querySelector('[data-m12c-engrave-save]');
      if (saveBtn) saveBtn.disabled = true;

      const thumbName = this.root.querySelector('[data-m12c-engrave-thumb-name]');
      if (thumbName) { thumbName.textContent = ''; thumbName.style.fontSize = ''; }

      const popup = this.root.querySelector('[data-m12c-engrave-popup]');
      if (!popup) return;
      const input = popup.querySelector('[data-m12c-engrave-input]');
      const popupName = popup.querySelector('[data-m12c-engrave-popup-name]');
      const clearBtn = popup.querySelector('[data-m12c-engrave-clear]');
      if (input) input.value = '';
      if (popupName) { popupName.textContent = ''; popupName.style.fontSize = ''; }
      if (clearBtn) clearBtn.classList.remove('is-visible');
    }

    _showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.removeAttribute('hidden');
      const msgEl = this.errorEl.querySelector('.m12c__error-text');
      if (msgEl) msgEl.textContent = msg;
    }

    _hideError() {
      if (!this.errorEl) return;
      this.errorEl.setAttribute('hidden', '');
    }
  }

  // ─────────────────────────────────────────
  //  Boot
  // ─────────────────────────────────────────
  function initAll() {
    document.querySelectorAll('[data-m12c-section]').forEach((el) => {
      if (!el._m12cInstance) {
        el._m12cInstance = new Mini12Camera(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
